import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

import { getPartnerCompanyApprovalById } from 'src/api/partner';
import { getVerifyPermission } from '@repo/shared/helpers';
import { determineUserProps } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { TaskViewer } from '@repo/components';
import { GeneralNotFound, NotFound } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/approvals/$approvalId')({
	component: RouteComponent,
});

function RouteComponent() {
	const [_originalFormData, setOriginalFormData] = useState({});
	// const [isSubmitted, setIsSubmitted] = useState(false);
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, approvalId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useBreadcrumbLevel(1, 'Approvals', `/partner/${partnerId}/approvals`);
	useBreadcrumbLevel(2, 'Approval Details');

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const _permissions = {
		taskDo: verifyPermission([PERMISSIONS.partnerCompanyTasksDo]),
		taskDelete: undefined,
	};

	const {
		data: approval,
		isLoading: approvalLoading,
		error: approvalError,
	} = useQuery({
		queryKey: ['partner-company-approval', partnerId, approvalId],
		queryFn: () => getPartnerCompanyApprovalById(partnerId, approvalId),
		enabled: !userLoading && !!authenticatedUser,
	});

	useEffect(() => {
		if (!approval || approvalLoading || userLoading || approvalError) return;
		if (approval.status === 'todo' && !approval.result) {
			const data = {};
			const userProps = determineUserProps(approval, approval?.user);
			if (userProps) {
				data.userProps = userProps;
			}
			setOriginalFormData(data);
		} else {
			setOriginalFormData(approval.result);
		}
	}, [approval, approvalLoading, userLoading, approvalError]);

	const isLoading = userLoading || approvalLoading;

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!approval || approvalError) {
		return (
			<NotFound>
				<div className="w-[300px]">
					<NotFound.Illustration src="/public/empty_box.svg" />
				</div>
				<NotFound.Label>We couldn't find this task. Maybe it has been deleted?</NotFound.Label>
				<NotFound.Actions>
					<NotFound.Actions.Action onClick={() => navigate({ to: `/partner/${partnerId}/tasks` })} variant="outlined">
						Return to tasks
					</NotFound.Actions.Action>
				</NotFound.Actions>
			</NotFound>
		);
	}

	const _handleUpdate = async () => {
		// Invalidate queries to refetch data
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-approval', partnerId, approvalId],
		});
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-approvals', partnerId],
		});
	};

	const handleClose = () => {
		navigate({ to: `/partner/${partnerId}/approvals` });
		return null;
	};

	const handleOpenDocument = (documentId: string) => {
		navigate({ to: `/partner/${partnerId}/documents/${documentId}` });
		return null;
	};

	// const handleChange = async ({ ...data }) => {
	// 	const t = Object.assign({}, approval);
	// 	t.result = data;

	// 	try {
	// 		await request.put(`/api/tasks/${approvalId}/draft`, t);
	// 	} catch (e) {
	// 		console.error(e);
	// 	}
	// };

	// const handleSubmit = async ({ ...data }) => {
	// 	if (isSubmitted) {
	// 		EventEmitter.emit('toast', {
	// 			type: 'warning',
	// 			message: 'Task has already been submitted',
	// 		});
	// 		return;
	// 	}

	// 	const t = Object.assign({}, approval);
	// 	t.status = Object.values(data).find((d) => d.isFailed) ? 'failed' : 'done';
	// 	t.result = data;

	// 	try {
	// 		setIsSubmitted(true);
	// 		await request.put(`/api/tasks/${approvalId}`, t);
	// 		EventEmitter.emit('toast', {
	// 			type: 'success',
	// 			message: 'Task has been submitted',
	// 		});
	// 	} catch (e) {
	// 		setIsSubmitted(false);
	// 		console.error(e);
	// 		EventEmitter.emit('toast', {
	// 			type: 'error',
	// 			message: 'Error submitting task!',
	// 		});
	// 	}

	// 	handleUpdate();
	// 	handleClose();
	// };

	return (
		<TaskViewer
			task={approval}
			// documents={documents}
			// onUpdate={onUpdate}
			onClose={handleClose}
			onOpenDocument={(documentId) => handleOpenDocument(documentId)}
			permissions={{
				taskDo: verifyPermission(PERMISSIONS.partnerCompanyApprovalsTasksDo),
				taskDelete: undefined,
			}}
		/>
	);
}
